import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import dayjs from "dayjs";
import { withProcedureWraper } from "../..";
import { Routes } from "../../../../AppRoutes";
import { Table, UserManual } from "../../../../components";
import { Box } from "../../../../components/box-v2";
import { Icon } from "../../../../components/icon";
import { requestStatus } from "../../../../constants";
import { IDepartmentTree } from "../../../../services/vos/business-tickets/department.service";
import { useSelector } from "../../../../store/store";
import CreateRequestButton from "../../components/CreateRequestButton";
import FilterTickets, { FilterFormValues } from "./components/filter-tickets";
import { useBusinessTicketsRequests } from "./hooks";
import { useBusinessTicketsColumn } from "./hooks/use-business-tickets-column";
import useDepartmentTree from "./hooks/use-department-tree";

const isDateInRange = (date: any, fromDate: any, toDate: any) => {
  const dateOnly = dayjs(date).startOf("day");
  const fromDateOnly = dayjs(fromDate).startOf("day");
  const toDateOnly = dayjs(toDate).startOf("day");
  return (
    (dateOnly.isAfter(fromDateOnly) || dateOnly.isSame(fromDateOnly)) &&
    (dateOnly.isBefore(toDateOnly) || dateOnly.isSame(toDateOnly))
  );
};

const flattenDepartmentTree = (departments: IDepartmentTree[]): IDepartmentTree[] => {
  const result: IDepartmentTree[] = [];
  const flatten = (items: IDepartmentTree[]) => {
    items.forEach((item) => {
      result.push(item);
      if (item.ChildDepartment?.length) {
        flatten(item.ChildDepartment);
      }
    });
  };
  flatten(departments);
  return result;
};

const checkInDepartmentTree = (departmentTree: IDepartmentTree | null, departmentFilter: string): boolean => {
  if (!departmentTree) return false;

  if (departmentTree.Dep_ID.toString() === departmentFilter) return true;

  return (
    departmentTree.ChildDepartment?.some((child: IDepartmentTree) => checkInDepartmentTree(child, departmentFilter)) ??
    false
  );
};

const checkStatusInSteps = (steps: any[], handledStepIndex: number, status: string, email: string) => {
  const stepsLength = steps.length;

  if (status === requestStatus.submitted) {
    if (handledStepIndex === -1) return false;
    if (handledStepIndex === stepsLength - 1) return false;

    const nextStep = steps[handledStepIndex + 1];

    if (!nextStep) return false;

    return nextStep.some((approver: any) => approver.email.toLowerCase() === email.toLowerCase());
  }

  for (let i = 1; i <= handledStepIndex; i++) {
    const step = steps[i];
    if (
      step.some(
        (approver: any) =>
          (status === requestStatus.completed ? approver.isApproved : approver.isRejected) &&
          approver.email.toLowerCase() === email.toLowerCase(),
      )
    ) {
      return true;
    }
  }

  return false;
};

export const procedureCollaborateTicket: FC = withProcedureWraper(() => {
  const history = useHistory();
  const { columns } = useBusinessTicketsColumn();

  const user = useSelector((state) => state.user);
  const userEmail = user?.current?.username;

  const {
    mappedBusinessTicketsMyRequests,
    businessTicketsRequestsNeedToApprove = [],
    mappedBusinessTicketsRequests = [],
    isLoading,
    guideLink,
  } = useBusinessTicketsRequests();

  const {
    data: departmentTree,
    isLoading: isLoadingDepartmentTree,
    isFetching: isFetchingDepartmentTree,
  } = useDepartmentTree();

  const loadingDepartmentTree = isLoadingDepartmentTree || isFetchingDepartmentTree || isLoading;

  const flattenedDepartmentTree = useMemo(() => {
    return departmentTree?.Data ? flattenDepartmentTree(departmentTree.Data) : [];
  }, [departmentTree?.Data]);

  const [myFilters, setMyFilters] = useState<FilterFormValues>({
    dateRange: undefined,
    status: undefined,
    department: undefined,
  });
  const [needToApproveFilters, setNeedToApproveFilters] = useState<FilterFormValues>({
    dateRange: undefined,
    status: undefined,
    department: undefined,
  });

  const filterTickets = useCallback(
    (item: any, dateRange: any, department: any) => {
      const createdDateTime = item.createdDateTime;
      const eWorkingTime = item.eWorkingTime;
      const eFinishTime = item.eFinishTime;

      const createdInRange = dateRange ? isDateInRange(createdDateTime, dateRange?.[0], dateRange?.[1]) : true;
      const workingInRange = dateRange ? isDateInRange(eWorkingTime, dateRange?.[0], dateRange?.[1]) : true;
      const finishInRange = dateRange ? isDateInRange(eFinishTime, dateRange?.[0], dateRange?.[1]) : true;

      const departmentScope = item.DepartmentScope && item.DepartmentScope.split("|").at(0);
      const departmentFilter = department && department.split("|").at(0);
      const selectedDepartment = flattenedDepartmentTree.reduce(
        (acc: IDepartmentTree | null, item: IDepartmentTree) => {
          if (item.Dep_ID.toString() === departmentFilter) {
            return item;
          }

          return acc;
        },
        null,
      );

      const departmentInRange = department
        ? departmentScope
          ? selectedDepartment
            ? checkInDepartmentTree(selectedDepartment, departmentScope)
            : false
          : false
        : true;

      return (createdInRange || workingInRange || finishInRange) && departmentInRange;
    },
    [flattenedDepartmentTree],
  );

  const filteredMyRequests = useMemo(() => {
    if (!mappedBusinessTicketsMyRequests) return [];
    if (!myFilters.dateRange && !myFilters.status && !myFilters.department) return mappedBusinessTicketsMyRequests;

    return mappedBusinessTicketsMyRequests.filter((item) => {
      const statusInRange = myFilters.status ? item.eRequestStatus === myFilters.status : true;

      return filterTickets(item, myFilters.dateRange, myFilters.department) && statusInRange;
    });
  }, [filterTickets, mappedBusinessTicketsMyRequests, myFilters.dateRange, myFilters.department, myFilters.status]);

  const filteredNeedToApproveRequests = useMemo(() => {
    if (!businessTicketsRequestsNeedToApprove) return [];
    if (!needToApproveFilters.dateRange && !needToApproveFilters.status && !needToApproveFilters.department)
      return businessTicketsRequestsNeedToApprove;

    return mappedBusinessTicketsRequests.filter((item) => {
      const steps = JSON.parse(item?.eSteps || "[]") || [];

      const stepsLength = steps.length;

      if (!stepsLength) return false;

      const handledStepIndex = steps.findLastIndex((step: any) =>
        step.some((approver: any) => approver.isApproved || approver.isRejected),
      );

      const statusInRange = needToApproveFilters.status
        ? checkStatusInSteps(steps, handledStepIndex, needToApproveFilters.status, userEmail)
        : true;

      return filterTickets(item, needToApproveFilters.dateRange, needToApproveFilters.department) && statusInRange;
    });
  }, [
    businessTicketsRequestsNeedToApprove,
    needToApproveFilters.dateRange,
    needToApproveFilters.status,
    needToApproveFilters.department,
    mappedBusinessTicketsRequests,
    userEmail,
    filterTickets,
  ]);

  const handleMyFilter = (values: FilterFormValues) => {
    setMyFilters(values);
  };

  const handleMyReset = () => {
    setMyFilters({
      dateRange: undefined,
      status: undefined,
      department: undefined,
    });
  };

  const handleNeedToApproveFilter = (values: FilterFormValues) => {
    setNeedToApproveFilters(values);
  };

  const handleNeedToApproveReset = () => {
    setNeedToApproveFilters({
      dateRange: undefined,
      status: undefined,
      department: undefined,
    });
  };

  // const filteredDataSource = useMemo(() => {
  //   let dataSource = [...mappedBusinessTicketsRequests];
  //   if (filters.eRequestStatus) {
  //     dataSource = dataSource.filter((e: any) => compareRequestStatus(e.eRequestStatus, filters?.eRequestStatus!));
  //   }
  //   if (filters.createdDateTime) {
  //     const [startDate, endDate]: [Moment, Moment] = filters.createdDateTime;
  //     dataSource = dataSource.filter((e: any) => {
  //       const createDate = moment(e.createdDateTime);
  //       return isDateInRange(createDate, startDate.startOf("day"), endDate.endOf("day"));
  //     });
  //   }

  //   return dataSource;
  // }, [filters, mappedBusinessTicketsRequests]);

  useEffect(() => {
    const styleId = "hide-table-body-style-business-tickets";
    const existingStyle = document.getElementById(styleId);
    const shouldHideBody = !mappedBusinessTicketsMyRequests?.length || !businessTicketsRequestsNeedToApprove?.length;

    if (shouldHideBody) {
      if (!existingStyle) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `.hide-table-body-wrapper .ant-table-tbody { display: none !important; }`;
        document.head.appendChild(style);
      }
    } else {
      if (existingStyle) {
        existingStyle.remove();
      }
    }

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [mappedBusinessTicketsMyRequests?.length, businessTicketsRequestsNeedToApprove?.length]);

  return (
    <div className="ITCodeGrantedPage">
      <div className="pd15">
        <UserManual title="CẤP/HOÀN VÉ CÔNG TÁC" icon={<Icon.CarCV />} guideLink={guideLink} />
        <Box
          collapsible
          title="Yêu cầu của tôi"
          titleRight={
            <div className="flex gap-2 items-center">
              <FilterTickets filters={myFilters} onFilter={handleMyFilter} onReset={handleMyReset} />

              <CreateRequestButton
                className="!m-0"
                onClick={() => history.push(Routes.createProcedureCollaborateTicket.path.replace(":id", "0"))}
              />
            </div>
          }
        >
          <div className={!filteredMyRequests?.length ? "hide-table-body-wrapper" : undefined}>
            <Table
              loading={loadingDepartmentTree}
              rowKey="id"
              columns={columns}
              dataSource={filteredMyRequests}
              scroll={{
                x: "max-content",
              }}
              locale={{
                emptyText: "Không có dữ liệu",
              }}
              onRow={(record) => ({
                onClick: () =>
                  history.push(Routes.myProcedureCollaborateTicketDetailPage.path.replace(":id", record.id)),
                style: { cursor: "pointer" },
              })}
              pagination={{
                pageSize: 8,
              }}
            />
          </div>
        </Box>

        <Box
          collapsible
          title="Các yêu cầu cần duyệt"
          titleRight={
            <FilterTickets
              hideDraft
              filters={needToApproveFilters}
              onFilter={handleNeedToApproveFilter}
              onReset={handleNeedToApproveReset}
            />
          }
        >
          <div className={!filteredNeedToApproveRequests?.length ? "hide-table-body-wrapper" : undefined}>
            <Table
              loading={loadingDepartmentTree}
              rowKey="id"
              columns={columns}
              dataSource={filteredNeedToApproveRequests}
              locale={{
                emptyText: "Không có dữ liệu",
              }}
              onRow={(record) => ({
                onClick: () => history.push(Routes.procedureCollaborateTicketDetailPage.path.replace(":id", record.id)),
                style: { cursor: "pointer" },
              })}
              scroll={{
                x: "max-content",
              }}
              pagination={{
                pageSize: 8,
              }}
            />
          </div>
        </Box>
      </div>
    </div>
  );
});
